import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import debounce from "lodash/debounce"

import { Asset, AssetCollection, Link as HALLink, Index } from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

import { BackendContext } from "@/context"

const RESULT_LIMIT = "5"
const SEARCH_DEBOUNCE_MS = 500

/**
 * AssetLinkModal searches existing assets by symbol or name and hands the one
 * chosen to onLink.
 */
export function AssetLinkModal({
  isOpen,
  onClose,
  onLink,
}: {
  isOpen: boolean
  onClose: () => void
  onLink: (assetID: string) => Promise<void>
}) {
  const backend = useContext(BackendContext)

  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Asset[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Each search takes a ticket; only the latest one may show what it found.
  const latestSearch = useRef(0)

  // A selection only stands while it is one of the results on screen.
  const showResults = useCallback((assets: Asset[]) => {
    setResults(assets)
    setSelected((id) => (assets.some((asset) => asset.id === id) ? id : null))
  }, [])

  const runSearch = useCallback(
    async (q: string) => {
      const ticket = ++latestSearch.current

      try {
        const data = backend.link("data").resolve() as Index
        await data.fetch({ reload: true })

        const link = data.link("assets")
        if (!link) {
          throw new Error("Assets resource is not available")
        }

        // A Link memoizes what it resolves to, so the search needs a
        // collection of its own.
        const href = new URL(link.href.toString())
        href.searchParams.set("q", q)
        href.searchParams.set("limit", RESULT_LIMIT)

        const assets = new HALLink(href, link.type).resolve() as AssetCollection
        await assets.fetch({ reload: true })

        if (ticket === latestSearch.current) {
          showResults(assets.models)
        }
      } catch (err) {
        if (ticket === latestSearch.current) {
          setError(err instanceof Error ? err.message : String(err))
        }
      } finally {
        if (ticket === latestSearch.current) {
          setSearching(false)
        }
      }
    },
    [backend, showResults],
  )

  const debouncedSearch = useMemo(() => debounce(runSearch, SEARCH_DEBOUNCE_MS), [runSearch])

  const cancelSearch = useCallback(() => {
    debouncedSearch.cancel()
    latestSearch.current++
    setSearching(false)
  }, [debouncedSearch])

  useEffect(() => cancelSearch, [cancelSearch])

  useEffect(() => {
    if (!isOpen) {
      cancelSearch()
      setQuery("")
      setResults([])
      setSelected(null)
      setError(null)
    }
  }, [isOpen, cancelSearch])

  function onChangeText(text: string) {
    setQuery(text)
    setError(null)

    const q = text.trim()
    if (q === "") {
      cancelSearch()
      showResults([])
      return
    }

    setSearching(true)
    debouncedSearch(q)
  }

  async function submit() {
    if (!selected) {
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await onLink(selected)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalBackdrop />
      <ModalContent className="bg-background-secondary border border-border-medium max-w-lg w-full">
        <ModalHeader>
          <Text className="text-lg font-semibold text-foreground-primary">Link an asset</Text>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Input
              variant="outline"
              size="md"
            >
              <InputField
                value={query}
                onChangeText={onChangeText}
                placeholder="Search by symbol or name"
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
              />
            </Input>

            {searching ? <Spinner size="small" /> : null}

            <VStack className="gap-2">
              {results.map((asset) => (
                <Pressable
                  key={asset.id}
                  onPress={() => setSelected(asset.id)}
                  className={`rounded-md border px-3 py-2 ${
                    selected === asset.id ? "border-border-focused bg-background-tertiary" : "border-border-medium"
                  }`}
                >
                  <HStack className="items-center gap-3">
                    <Text className="text-sm font-semibold text-foreground-primary">{asset.attributes.symbol}</Text>
                    <Text className="flex-1 text-sm text-foreground-secondary">{asset.attributes.name}</Text>
                  </HStack>
                </Pressable>
              ))}
              {!searching && query.trim() !== "" && results.length === 0 && !error ? (
                <Text className="text-sm text-foreground-secondary">No matching assets.</Text>
              ) : null}
            </VStack>

            {error ? (
              <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
                <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter className="gap-2">
          <Button
            variant="outline"
            onPress={onClose}
            isDisabled={submitting}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            onPress={() => void submit()}
            isDisabled={!selected || submitting}
            className="bg-background-contrast"
          >
            <ButtonText className="text-foreground-contrast">{submitting ? "Linking…" : "Link"}</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
